/* eslint-disable camelcase */
import isValidSkuId from 'ai/utils/isValidSkuId';

const VARIANT_MODE = {
    FALLBACK: 'fallback',
    SELECTOR: 'selector',
    NONE: 'none'
};

const getDisplayOrder = value => {
    const hasNumericValue = (typeof value === 'number' && Number.isFinite(value))
        || (typeof value === 'string' && value.trim() && Number.isFinite(Number(value)));

    return hasNumericValue ? Number(value) : Number.MAX_SAFE_INTEGER;
};

/**
 * Returns valid variations in their original order. Duplicate SKU IDs use first-wins semantics,
 * matching the socket normalizer and preventing two controls from representing one selection.
 */
const getValidVariations = variations => {
    if (!Array.isArray(variations)) {
        return [];
    }

    const seenSkuIds = new Set();

    return variations.filter(variation => {
        const skuId = variation?.sku_id;

        if (
            !variation
            || typeof variation !== 'object'
            || Array.isArray(variation)
            || !isValidSkuId(skuId)
            || seenSkuIds.has(skuId)
        ) {
            return false;
        }

        seenSkuIds.add(skuId);

        return true;
    });
};

/** Sorts a new array so caller-owned product data is never mutated. */
const getOrderedValidVariations = variations =>
    getValidVariations(variations).sort((variationA, variationB) =>
        getDisplayOrder(variationA.display_order) - getDisplayOrder(variationB.display_order));

const getProductKey = product => {
    if (!product || typeof product !== 'object' || Array.isArray(product)) {
        return null;
    }

    if (isValidSkuId(product.id)) {
        return product.id;
    }

    return isValidSkuId(product.default_sku) ? product.default_sku : null;
};

const isVariationSku = (variations, skuId) =>
    isValidSkuId(skuId)
    && Array.isArray(variations)
    && variations.some(variation => variation?.sku_id === skuId);

const getSelectedVariation = (variations, selectedSkuId) => {
    if (!isValidSkuId(selectedSkuId)) {
        return null;
    }

    return getValidVariations(variations).find(variation => variation.sku_id === selectedSkuId) || null;
};

/**
 * Returns the one SKU that drives both the selector and downstream product actions.
 */
const resolveProductSkuId = (product, storedSkuId, enabled) => {
    if (!product || typeof product !== 'object' || Array.isArray(product)) {
        return null;
    }

    const defaultSkuId = isValidSkuId(product.default_sku) ? product.default_sku : null;

    if (!enabled) {
        return defaultSkuId;
    }

    const variations = getOrderedValidVariations(product.variations);

    if (variations.length === 0) {
        return defaultSkuId;
    }

    if (isVariationSku(variations, storedSkuId)) {
        return storedSkuId;
    }

    if (isVariationSku(variations, product.pre_selected_sku_id)) {
        return product.pre_selected_sku_id;
    }

    if (isVariationSku(variations, defaultSkuId)) {
        return defaultSkuId;
    }

    return variations[0].sku_id;
};

const setSkuSelection = (selections, productKey, skuId) => ({
    ...selections,
    [productKey]: skuId
});

const areSelectionsEqual = (currentSelections, nextSelections) => {
    const currentKeys = Object.keys(currentSelections);
    const nextKeys = Object.keys(nextSelections);

    return currentKeys.length === nextKeys.length
        && currentKeys.every(key => currentSelections[key] === nextSelections[key]);
};

const reconcileSelections = (currentSelections, products, enabled) => {
    if (!enabled || !Array.isArray(products)) {
        return Object.keys(currentSelections).length === 0 ? currentSelections : {};
    }

    const nextSelections = products.reduce((selections, product) => {
        const productKey = getProductKey(product);

        if (productKey === null || Object.prototype.hasOwnProperty.call(selections, productKey)) {
            return selections;
        }

        const resolvedSkuId = resolveProductSkuId(product, currentSelections[productKey], true);

        return isValidSkuId(resolvedSkuId)
            ? setSkuSelection(selections, productKey, resolvedSkuId)
            : selections;
    }, {});

    return areSelectionsEqual(currentSelections, nextSelections) ? currentSelections : nextSelections;
};

const getVariantMode = product => {
    if (!product || typeof product !== 'object' || Array.isArray(product)) {
        return VARIANT_MODE.NONE;
    }

    if (product.fallback_reason && typeof product.fallback_text === 'string' && product.fallback_text.trim()) {
        return VARIANT_MODE.FALLBACK;
    }

    return getValidVariations(product.variations).length > 0
        ? VARIANT_MODE.SELECTOR
        : VARIANT_MODE.NONE;
};

const getOptionalString = value => (typeof value === 'string' && value.trim() ? value : null);

/**
 * Projects selected variation card fields without mutating the source product. A missing selected
 * sale price intentionally clears a base sale price so stale pricing is not displayed.
 */
const getProductWithSelectedVariation = (product, selectedSkuId) => {
    if (!product || typeof product !== 'object' || Array.isArray(product)) {
        return product;
    }

    const selectedVariation = getSelectedVariation(product.variations, selectedSkuId);

    if (!selectedVariation) {
        return product;
    }

    return {
        ...product,
        image_url: getOptionalString(selectedVariation.image_url) || product.image_url,
        list_price: getOptionalString(selectedVariation.list_price) || product.list_price,
        sale_price: getOptionalString(selectedVariation.sale_price),
        size: getOptionalString(selectedVariation.size) || product.size
    };
};

const getVariantAwarePdpUrl = (product, selectedSkuId) => {
    const url = getOptionalString(product?.url);

    if (!url || !isVariationSku(product?.variations, selectedSkuId)) {
        return product?.url;
    }

    if (/[?&]skuId=/.test(url)) {
        return url.replace(/([?&]skuId=)[^&]*/, `$1${selectedSkuId}`);
    }

    return `${url}${url.includes('?') ? '&' : '?'}skuId=${selectedSkuId}`;
};

export {
    VARIANT_MODE,
    getDisplayOrder,
    getOrderedValidVariations,
    getProductKey,
    getProductWithSelectedVariation,
    getSelectedVariation,
    getValidVariations,
    getVariantAwarePdpUrl,
    getVariantMode,
    isValidSkuId,
    isVariationSku,
    reconcileSelections,
    resolveProductSkuId,
    setSkuSelection
};
